import PropTypes from "prop-types";
import { NumericFormat } from 'react-number-format';
import * as yup from "yup";

export const validationSchema = yup.object().shape({
  // Element validation - support both old and new forms
  elementName: yup.mixed().when(['isElementOther'], {
    is: (isElementOther) => isElementOther,
    then: () => yup.object().shape({
      title: yup.string().required("This is a Required field"),
      language: yup.string().required("This is a Required field"),
      value: yup.string().required("This is a Required field"),
      type: yup.string()
    }),
    otherwise: () => yup.mixed().nullable()
  }),
  elementType: yup.string().when(['isElementOther'], {
    is: (isElementOther) => !isElementOther,
    then: () => yup.string().required("Element type is required"),
    otherwise: () => yup.string().nullable()
  }),
  riskType: yup.string().required("This is a Required field"),
  defectType: yup.string().required("This is a Required field"),
  priority: yup.string().required("Priority is required"),
  priorityType: yup.string().nullable(),
  defectDescription: yup.string().required("This is a Required field"),
  measureDescription: yup.string().required("This is a Required field"),
  pricePerUnit: yup.number().when(["costDefect"], {
    is: true,
    then:() => yup
      .number()
      .required("This is a Required field")
      .min(1)
      .typeError("This should be a valid Cost"),
    otherwise:()=> yup.number().nullable(),
  }),
  quantity:yup.number().when(["costDefect"], {
    is: true,
    then:() => yup
      .number()
      .required("This is a Required field")
      .min(1)
      .typeError("This should be a valid Quantity"),
    otherwise:()=> yup.number().nullable(),
  }),
  extraCostComment: yup.string().when(["extraCost", "costDefect"], {
    is: (extraCost, costDefect) =>
      extraCost != null && Number(extraCost) > 0 && costDefect,
    then:() => yup
      .string()
      .required("This is a Required field")
      .typeError("This is a Required field"),
    otherwise:()=> yup.string().nullable(),
  }),
  costComment: yup.string().when(["extraCost", "costDefect"], {
    is: (extraCost, costDefect) =>
      extraCost != null && Number(extraCost) > 0 && costDefect,
    then:() => yup
      .string()
      .required("This is a Required field")
      .typeError("This is a Required field"),
    otherwise:()=> yup.string().nullable(),
  }),
  unit: yup.string().when(["costDefect"], {
    is: true,
    then:() => yup
      .string()
      .required("This is a Required field")
      .typeError("This should be a valid Quantity"),
    otherwise:() => yup.string().nullable(),
  }),
  customElement: yup.mixed().when(["isElementOther"], {
    is: true,
    then:() => yup.object().shape({
      value: yup.string().required("This is required"),
      title: yup.string().required("This is required"),
    }),
    otherwise:() => yup.mixed().notRequired(),
  }),
});

export const NumericFormatCustom = (props) => {
  const { inputRef, onChange, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator="."
      decimalSeparator=","
      isNumericString
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values;
        if (floatValue == null) {
          return formattedValue === "";
        } else {
          return floatValue > 0;
        }
      }}
    />
  );
};

NumericFormatCustom.propTypes = {
  inputRef: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export const NumberFormatForPercentage = (props) => {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumericFormat 
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator="."
      decimalSeparator=","
      isNumericString
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values;
        if (floatValue == null) {
          return formattedValue === "";
        } else {
          return floatValue <= 100 && floatValue > 0;
        }
      }}
    />
  );
};

NumberFormatForPercentage.propTypes = {
  inputRef: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};


export const calulateTotal = (values, includeMultiple) => {
  let total = 0;
  if (values.pricePerUnit > 0 && values.quantity > 0) {
    total = values.pricePerUnit * values.quantity;
    if (values.elementAffected > 0) {
      total = total * (values.elementAffected / 100);
    }
    if (values.extraCost > 0) {
      total = total + Number(values.extraCost);
    }
    if (values.isMultiple && includeMultiple) {
      total =
        Math.round((total * values.multipleCost?.short) / 1000) * 1000 +
        Math.round((total * values.multipleCost?.medium) / 1000) * 1000 +
        Math.round((total * values.multipleCost?.long) / 1000) * 1000;
    }
    return total;
  }
  return total;
};

export const convertToDecimal = (value,isMultiple) => {
  return parseFloat(calulateTotal(value,isMultiple)).toFixed(2)
}

export const createElementDto = (values) => {
  console.log('=== CREATE ELEMENT DTO ===');
  console.log('Input values:', values);
  
  // Handle both old form (elementName object) and new form (elementType string) structures
  const isOthersElement = values.isElementOther;
  
  // Enhanced logic to handle both old and new form structures
  let elementValue = '';
  let parentIdent = '';
  
  if (isOthersElement) {
    // For "Others" elements, get custom element name
    elementValue = values.customElement?.value || values.elementName || values.elementType || '';
    parentIdent = values.customElement?.parentIdent || values.elementName?.parentIdent || values.subCategory?.ident || '';
  } else {
    // For regular elements
    if (values.elementName && typeof values.elementName === 'object') {
      // Old form structure - elementName is an object
      elementValue = values.elementName.value || '';
      parentIdent = values.elementName.parentIdent || '';
    } else {
      // New form structure - elementType is a string
      elementValue = values.elementType || values.elementName || '';
      // For new form, we need to get parentIdent from subCategory
      parentIdent = values.subCategory?.ident || values.elementName?.parentIdent || '';
    }
  }

  console.log('Element processing:');
  console.log('- isOthersElement:', isOthersElement);
  console.log('- elementValue:', elementValue);
  console.log('- parentIdent:', parentIdent);
  console.log('- values.elementType:', values.elementType);
  console.log('- values.elementName:', values.elementName);
  console.log('- values.subCategory:', values.subCategory);

  const el = {
    type: "elementDto",
    trade: elementValue, 
    name: elementValue,
    elementName: isOthersElement
        ? [
            {
              type: "title",
              title: elementValue,
              language: "de",
              value: elementValue,
            },
            {
              type: "title",
              title: elementValue,
              language: "en",
              value: elementValue,
            },
          ]
        : null,
    constructionYear: !!values.constructionYear ? values.constructionYear : null,
    subCategorySecondaryIdent: parentIdent,
  };
  
  console.log('Created element DTO:', el);
  console.log('=== END ELEMENT DTO ===');
  return el;
};

export const createMeasureDto = (
  values
) => {
  // Handle both priority (old form) and priorityType (new form) fields
  const priority = values.priority || values.priorityType || "kurzfristig";
  
  return {
    type: "measureDto",
    measureDescription: values.measureDescription,
    pricePerUnit: values.pricePerUnit,
    quantity: values.quantity,
    priority: priority,
    percentageShortTermPriority: values.percentageShortTermPriority || 0,
    percentageMidTermPriority: values.percentageMidTermPriority || 0,
    percentageLongTermPriority: values.percentageLongTermPriority || 0,
    extraCostComment: values.extraCostComment || values.costComment || "",
    extraCost: values.extraCost,
    elementAffected: values.elementAffected || 0,
    probabilityOfOccurrence: values.probabilityOfOccurrence || 0,
    comments: values.comments || "",
    unit: values.unit,
    active: true,
    totalCost: calulateTotal(values,false),
    code: values.costParameters?.code,
    costElement: values.costParameters?.name,
  };
};

export const createDefectDto = (values) => {
  return {
    type: "defectDto",
    risk: values.riskType,
    inRedFlag: values.inRedFlag,
    defect: values.defectDescription,
    defectType: values.defectType,
    costDefect: values.costDefect,
    dataRoomIndex: values.dataRoomIndex, // Maps to "Data room Index" field
    quelle: values.quelle,
    questionAndAnswer: values.questionAndAnswer || values.qaField, // Support both old and new field names
    comments: values.comments, // New comments field
    code: null,
  };
};

export const getLanguageBasedOption = (option, langCode) => {
  if (option !== null && option !== "" && option.title !== undefined) {
    const tOption = option.title[langCode];
    if (tOption !== null) {
      return tOption;
    } else {
      return option.title["de"];
    }
  } else {
    return "";
  }
};

export const setDefaultValues = (defectType) =>{

  if(defectType ==="Lebenszyklus"){
    return {
      defectType : "Lebenszyklus",
      risk:"niedrig",
      priority:"langfristig",
      isRedFlag:false
    }
  } else if(defectType ==="Konformität"){
    return {
      defectType : "Konformität",
      risk:"hoch",
      priority:"kurzfristig",
      isRedFlag:true
    }
  } else if(defectType ==="Prävention"){
    return {
      defectType : "Prävention",
      risk:"niedrig",
      priority:"kurzfristig",
      isRedFlag:false
    }
  }else{
    return {
      defectType : "Defekt",
      risk:"mittel",
      priority:"kurzfristig",
      isRedFlag:false
    }
  }

}
export const setDefaultRiskTypeValues = (riskType) =>{

  if(riskType ==="hoch"){
    return {
      isRedFlag:true
    }
  } else{
    return {
      isRedFlag:false
    }
  }
}
export const formData = {
  elementName: {
    title: "",
    language: "",
    value: "",
    parentIdent:"",
  },
  elementType: "", // Added for enhanced form compatibility
  isElementOther: false,
  customElement: {
    value: "",
    title: {
      de: "",
      en: "",
    },
    parentIdent:"",
  },
  constructionYear: null,
  defectDescription: "",
  defectType: "Defekt",
  riskType: "mittel",
  priorityType: "kurzfristig", // Added for enhanced form compatibility
  inRedFlag: false,
  defectIdent: "",
  costDefect: true,
  dataRoomIndex: "",
  quelle: "",
  questionAndAnswer: "",
  qaField: "", // New Q&A field
  comments: "", // New comments field
  measureDescription: "",
  pricePerUnit: null,
  unit: "",
  quantity: 1,
  priority: "kurzfristig",
  isMultiple: false,
  multipleCost: {
    short: Number(30) / 100,
    medium: Number(40) / 100,
    long: Number(30) / 100,
  },
  priorityDistribution: { // Added for enhanced form compatibility
    shortTerm: 30,
    mediumTerm: 40,
    longTerm: 30
  },
  percentageShortTermPriority: 30,
  percentageMidTermPriority: 40,
  percentageLongTermPriority: 30,
  projectFactor: "",
  elementAffected: "",
  probabilityOfOccurrence: "",
  extraCost:"",
  extraCostComment:"",
  costComment: "", // Added for enhanced form compatibility
  measureIdent: "",
  comments: "",
  totalCost: "",
  costSuggestions:[],
  costParameters: null,
  photos: [], // Added for enhanced form compatibility
};

// Factory function to create fresh form data (prevents reference sharing issues)
export const createFreshFormData = () => {
  return {
    elementName: {
      title: "",
      language: "",
      value: "",
      parentIdent: "",
    },
    elementType: "",
    isElementOther: false,
    customElement: {
      value: "",
      title: {
        de: "",
        en: "",
      },
      parentIdent: "",
    },
    constructionYear: null,
    defectDescription: "",
    defectType: "Defekt",
    riskType: "mittel",
    priorityType: "kurzfristig",
    inRedFlag: false,
    defectIdent: "",
    costDefect: true,
    dataRoomIndex: "",
    quelle: "",
    questionAndAnswer: "",
    qaField: "",
    comments: "",
    measureDescription: "",
    pricePerUnit: null,
    unit: "",
    quantity: 1,
    priority: "kurzfristig",
    isMultiple: false,
    multipleCost: {
      short: Number(30) / 100,
      medium: Number(40) / 100,
      long: Number(30) / 100,
    },
    priorityDistribution: {
      shortTerm: 30,
      mediumTerm: 40,
      longTerm: 30
    },
    percentageShortTermPriority: 30,
    percentageMidTermPriority: 40,
    percentageLongTermPriority: 30,
    projectFactor: "",
    elementAffected: "",
    probabilityOfOccurrence: "",
    extraCost: "",
    extraCostComment: "",
    costComment: "",
    measureIdent: "",
    totalCost: "",
    costSuggestions: [],
    costParameters: null,
    photos: [],
  };
};

// ===== DEFECTS DATABASE INTEGRATION =====

// Search defects database based on user input - supports both German and English simultaneously
export const searchDefectsDatabase = (searchQuery = '', filters = {}, defectsData = null) => {
  if (!defectsData?.defects) {
    return [];
  }

  const query = searchQuery.toLowerCase().trim();
  
  if (!query) {
    return [];
  }

  const results = defectsData.defects.filter(defect => {
    // Search in both German and English content simultaneously
    const elementDe = defect.element?.de || '';
    const elementEn = defect.element?.en || '';
    const defectTypeDe = defect.defectType?.de || '';
    const defectTypeEn = defect.defectType?.en || '';
    const descriptionDe = defect.description?.de || '';
    const descriptionEn = defect.description?.en || '';
    const searchTermsDe = defect.searchTerms?.de || [];
    const searchTermsEn = defect.searchTerms?.en || [];

    // Check if query matches any field in either language
    const matchesElement = elementDe.toLowerCase().includes(query) || elementEn.toLowerCase().includes(query);
    const matchesDefectType = defectTypeDe.toLowerCase().includes(query) || defectTypeEn.toLowerCase().includes(query);
    const matchesDescription = descriptionDe.toLowerCase().includes(query) || descriptionEn.toLowerCase().includes(query);
    const matchesSearchTerms = 
      searchTermsDe.some(term => term.toLowerCase().includes(query) || query.includes(term.toLowerCase())) ||
      searchTermsEn.some(term => term.toLowerCase().includes(query) || query.includes(term.toLowerCase()));

    return matchesElement || matchesDefectType || matchesDescription || matchesSearchTerms;
  });
  
  return results;
};

// Get defect suggestions based on selected element
export const getDefectSuggestionsByElement = (elementName, defectsData, projectLanguage = 'de') => {
  if (!elementName || !defectsData) {
    return [];
  }

  // Extract the subcategory from hierarchical format "Category - Subcategory"
  let elementToMatch = elementName;
  if (elementName.includes(' - ')) {
    elementToMatch = elementName.split(' - ')[1] || elementName;
  }

  const elementLower = elementToMatch.toLowerCase();
  
  // Use defectsData directly (it's an array) or defectsData.defects if it's an object
  const defectsArray = Array.isArray(defectsData) ? defectsData : (defectsData.defects || []);
  
  // Direct matching without complex mappings - let the database search terms handle the complexity
  const matchedDefects = defectsArray.filter(defect => {
    // Handle both old format (defect.element.de) and new format (defect.element)
    const elementText = (defect.element?.de || defect.element || '').toLowerCase();
    const searchTerms = defect.searchTerms?.de || [];
    
    // Direct element text matching
    if (elementText.includes(elementLower)) {
      return true;
    }
    
    // Search terms matching
    if (searchTerms.some(term => term.toLowerCase().includes(elementLower) || elementLower.includes(term.toLowerCase()))) {
      return true;
    }
    
    return false;
  }).slice(0, 5); // Limit to top 5 suggestions
  
  return matchedDefects;
};

// Auto-fill form fields from selected defect
// Apply selected defect data to form fields with language-aware recommendations
export const applyDefectDatabaseToForm = (defect, setFieldValue, projectLanguage = 'de', showBilingual = false) => {
  if (!defect || !setFieldValue) {
    return;
  }

  // Helper function to get text with language fallback
  const getText = (textObj) => textObj?.[projectLanguage] || textObj?.de || textObj?.en || '';

  // Set defect description
  setFieldValue('defectDescription', getText(defect.description));
  
  // Set repair recommendation as measure description
  let measureDesc = '';
  
  if (showBilingual && defect.repairRecommendation?.de && defect.repairRecommendation?.en) {
    // Bilingual recommendation
    measureDesc = projectLanguage === 'en' 
      ? `${defect.repairRecommendation.en}\n\n[DE] ${defect.repairRecommendation.de}`
      : `${defect.repairRecommendation.de}\n\n[EN] ${defect.repairRecommendation.en}`;
  } else {
    // Single language recommendation with fallback
    const repairRecommendation = getText(defect.repairRecommendation);
    
    if (repairRecommendation) {
      measureDesc = repairRecommendation;
    } else {
      // Create fallback recommendation
      const elementName = getText(defect.element);
      const defectTypeName = getText(defect.defectType);
      
      measureDesc = projectLanguage === 'en' 
        ? `Repair measures for ${elementName}: ${defectTypeName}`
        : `Reparaturmaßnahmen für ${elementName}: ${defectTypeName}`;
    }
  }
  
  setFieldValue('measureDescription', measureDesc);
  
  // Set cost information from Excel structure
  setFieldValue('pricePerUnit', defect.cost?.average || defect.cost?.basePrice || 0);
  
  // Extract unit properly - remove € prefix if present
  let unit = defect.cost?.unit || 'm²';
  if (unit.startsWith('€/')) {
    unit = unit.substring(2); // Remove "€/" prefix
  }
  setFieldValue('unit', unit);
  setFieldValue('quantity', 1);
  
  // Use formMappings for dropdown values (Excel-aligned)
  if (defect.formMappings) {
    // Set German values directly
    setFieldValue('riskType', defect.formMappings.riskType || 'mittel');
    setFieldValue('defectType', defect.formMappings.defectType || 'Defekt');
    setFieldValue('priorityType', defect.formMappings.priorityType || 'mittelfristig');
  }
};
